import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import GuideButton from '../../common/guide/GuideButton';
import { parseTrAmount } from '../../../utils/numberFormat';

export interface ExpenseCashboxOption {
  id: string;
  name: string;
  currency: string;
  balance: number;
}

interface CashExpenseModalProps {
  open: boolean;
  onClose: () => void;
  /** Seçilebilecek kasalar (şube yöneticisinde yalnızca kendi şubesininkiler). */
  cashboxes: ExpenseCashboxOption[];
  /** Admin geçmiş tarihli gider girebilir; şube yöneticisinde tarih her zaman bugündür. */
  allowDate?: boolean;
  onSuccess: () => void;
}

const todayIso = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

interface ExpenseCategoryOption {
  id: number;
  name: string;
}

const CashExpenseModal: React.FC<CashExpenseModalProps> = ({ open, onClose, cashboxes, allowDate = false, onSuccess }) => {
  const [cashbox, setCashbox] = useState<ExpenseCashboxOption | null>(null);
  const [categories, setCategories] = useState<ExpenseCategoryOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [category, setCategory] = useState<ExpenseCategoryOption | null>(null);
  const [amountText, setAmountText] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCashbox(cashboxes.length === 1 ? cashboxes[0] : null);
    setCategory(null);
    setAmountText('');
    setReason('');
    setDate(todayIso());
    setError(null);
  }, [open, cashboxes]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCategoriesLoading(true);
    accountingApi.get(ACCOUNTING_API.CASHBOX_EXPENSE_CATEGORIES())
      .then(res => {
        if (cancelled) return;
        const list: any[] = res.data?.data || [];
        setCategories(list.map(c => ({ id: Number(c.id ?? c.Id), name: String(c.name ?? c.Name ?? '') })));
      })
      .catch(err => {
        if (!cancelled) setError(err?.response?.data?.message || 'Gider türleri yüklenemedi.');
      })
      .finally(() => { if (!cancelled) setCategoriesLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // "1.500" -> 1500, "1.250,50" -> 1250.5, "12.5" -> 12.5 (ortak Türkçe tutar okuyucu)
  const amount = parseTrAmount(amountText);
  const formatMoney = (value: number, currency: string) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency || 'TRY', minimumFractionDigits: 2 }).format(value);

  const validationError = useMemo(() => {
    if (!cashbox) return 'Kasa seçin.';
    if (!amountText.trim() || !(amount > 0)) return 'Geçerli bir tutar girin.';
    if (amount > cashbox.balance) return `Kasada yeterli bakiye yok (mevcut: ${formatMoney(cashbox.balance, cashbox.currency)}).`;
    if (!category) return 'Gider türünü seçin.';
    if (!reason.trim()) return 'Gider sebebini yazın.';
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashbox, amountText, amount, category, reason]);

  const handleSave = async () => {
    if (validationError || !cashbox) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        Amount: Math.round(amount * 100) / 100,
        Description: reason.trim(),
        ExpenseCategoryId: category?.id,
      };
      if (allowDate && date && date !== todayIso()) {
        body.TransactionDate = `${date}T12:00:00`;
      }
      const response = await accountingApi.post(ACCOUNTING_API.CASHBOX_EXPENSE(Number(cashbox.id)), body);
      if (response.data?.success === false) throw new Error(response.data?.message || 'Gider kaydedilemedi');
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Gider kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <ReceiptLongIcon color="error" />
        <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
          Kasadan Gider Ekle
        </Typography>
        <GuideButton guideKey="finance.cashExpense" size="small" />
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kasadan nakit alınarak yapılan harcamayı kaydedin. Tutar seçilen kasanın bakiyesinden düşülür ve kasa hareket raporunda gider olarak görünür.
        </Typography>

        {cashboxes.length === 0 ? (
          <Alert severity="info">Gider eklenebilecek kasa bulunamadı.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={cashboxes}
              value={cashbox}
              onChange={(_, v) => setCashbox(v)}
              getOptionLabel={o => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, o) => {
                const { key, ...rest } = props as any;
                return (
                  <li key={o.id} {...rest}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>{o.name}</span>
                      <Typography variant="body2" color="text.secondary">{formatMoney(o.balance, o.currency)}</Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  label="Nakdin alındığı kasa"
                  helperText={cashbox ? `Mevcut bakiye: ${formatMoney(cashbox.balance, cashbox.currency)}` : ' '}
                />
              )}
            />

            <TextField
              required
              label="Gider tutarı"
              value={amountText}
              onChange={e => setAmountText(e.target.value.replace(/[^\d.,]/g, ''))}
              inputMode="decimal"
              InputProps={{
                endAdornment: <InputAdornment position="end">{cashbox?.currency === 'TRY' || !cashbox ? '₺' : cashbox.currency}</InputAdornment>,
              }}
            />

            <Autocomplete
              options={categories}
              value={category}
              onChange={(_, v) => setCategory(v)}
              getOptionLabel={o => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={categoriesLoading}
              noOptionsText="Tanımlı gider türü yok"
              renderInput={params => <TextField {...params} required label="Gider türü" />}
            />

            <TextField
              required
              label="Gider sebebi"
              placeholder="Örn. Temizlik malzemesi alındı, tüp değişimi, kargo ücreti..."
              value={reason}
              onChange={e => setReason(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
            />

            {allowDate && (
              <TextField
                type="date"
                label="Gider tarihi"
                value={date}
                onChange={e => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayIso() }}
              />
            )}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>İptal</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSave}
          disabled={saving || cashboxes.length === 0 || !!validationError}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Gideri Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashExpenseModal;
